const LEGACY_PROJECT_REFS = ["ortrjyumexaemheeqqxn"];
const STALE_STORAGE_CLEANUP_KEY = "whisprai_stale_backend_storage_cleanup_v1";

type BackendConfigIssue = {
  title: string;
  detail: string;
};

function projectRefFromUrl(url: string | undefined): string | null {
  if (!url) return null;
  try {
    const host = new URL(url).hostname;
    const match = host.match(/^([^.]+)\.supabase\.co$/);
    return match?.[1] ?? null;
  } catch {
    return null;
  }
}

function decodeJwtPayload(token: string | undefined): Record<string, unknown> | null {
  if (!token) return null;
  try {
    const payload = token.split(".")[1];
    if (!payload) return null;
    const normalized = payload.replace(/-/g, "+").replace(/_/g, "/");
    const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, "=");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
}

function projectRefFromPublishableKey(key: string | undefined): string | null {
  const payload = decodeJwtPayload(key);
  const ref = payload?.ref;
  if (typeof ref === "string" && ref) return ref;

  const issuer = payload?.iss;
  if (typeof issuer === "string") return projectRefFromUrl(issuer);
  return null;
}

function removeStorageKeys(storage: Storage, shouldRemove: (key: string) => boolean) {
  const keys: string[] = [];
  for (let i = 0; i < storage.length; i += 1) {
    const key = storage.key(i);
    if (key && shouldRemove(key)) keys.push(key);
  }
  keys.forEach((key) => storage.removeItem(key));
}

export function clearLegacyBackendAuthStorage(): void {
  if (typeof window === "undefined") return;

  try {
    if (localStorage.getItem(STALE_STORAGE_CLEANUP_KEY) === "1") return;
  } catch { /* ignore */ }

  const isLegacyKey = (key: string) =>
    LEGACY_PROJECT_REFS.some((ref) => key.startsWith(`sb-${ref}-`) || key.includes(ref));

  try { removeStorageKeys(localStorage, isLegacyKey); } catch { /* ignore */ }
  try { removeStorageKeys(sessionStorage, isLegacyKey); } catch { /* ignore */ }
  try { localStorage.setItem(STALE_STORAGE_CLEANUP_KEY, "1"); } catch { /* ignore */ }
}

export function validateBackendConfig(): BackendConfigIssue | null {
  const configuredUrl = import.meta.env.VITE_SUPABASE_URL;
  const configuredProjectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
  const configuredKey = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

  const urlRef = projectRefFromUrl(configuredUrl);
  const keyRef = projectRefFromPublishableKey(configuredKey);
  const expectedRef = configuredProjectId || urlRef;

  if (!configuredUrl || !configuredKey || !expectedRef) {
    return {
      title: "Backend configuration missing",
      detail: "Lovable Cloud did not provide the required backend URL, project id, or publishable key for this build.",
    };
  }

  // Note: we intentionally do NOT hard-block on LEGACY_PROJECT_REFS here.
  // Legacy storage is already wiped by clearLegacyBackendAuthStorage(); blocking
  // app start traps users on a fallback screen even after a fresh build is served.

  if (urlRef && expectedRef && urlRef !== expectedRef) {
    return {
      title: "Backend URL mismatch",
      detail: `The backend URL belongs to ${urlRef}, but the project id is ${expectedRef}.`,
    };
  }

  if (keyRef && expectedRef && keyRef !== expectedRef) {
    return {
      title: "Backend key mismatch",
      detail: `The publishable key belongs to ${keyRef}, but the app is configured for ${expectedRef}.`,
    };
  }

  return null;
}